import { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import Big from 'big.js';
import { EarningWithholdingConceptDTO } from '../../interface/interface';
import { ItemVaucher, TaxType } from '../../interface/orderOfProvision';

export type PaidTax = {
    taxtype: number;
    amount: Big;
    percentage?: Big;
    taxAccount:number;
    taxName:string;
    taxAccountName:string;
};

type TaxesPaidContextType = {
    totalWithoutTaxes: Big,
    totalToPay: Big,
    setTotalToPay: Dispatch<SetStateAction<Big>>, // <-- Añadido
    earningWithHoldingConcept: EarningWithholdingConceptDTO,
    setEarningWithHoldingConcept: Dispatch<SetStateAction<EarningWithholdingConceptDTO>>
    paidTaxes: PaidTax[]; // Nueva propiedad
    updatePaidTax: (taxtype: number, taxAccount:number, taxName:string, amount: Big, taxAccountName:string, plusTaxes:boolean) => void; // Función para agregar
    updateTotalWithoutTaxes:(amount:Big) =>void
    cleanAll: () => void,
    totalTaxes:Big,
    vatNotTaxed:Big,
    exemptTax:Big,
    updateExemptTaxAmount: (amount:Big)=>void,
    updateVatNotTaxedAmount: (amount:Big) =>void,
    taxesList: TaxType[]
    setTaxesList:Dispatch<SetStateAction<TaxType[]>>
    convertDuplicateTaxes: (taxes:PaidTax[] | undefined) => PaidTax[]
    convertDuplicateItems: (items: ItemVaucher[] | undefined) => ItemVaucher[]
}


const TaxesPaidContext = createContext<TaxesPaidContextType>({
    totalWithoutTaxes: Big(0),
    setTotalToPay: () => {}, // No hace nada por defecto
    totalToPay: Big(0),
    earningWithHoldingConcept: { withholdingconcept: 0, description: '-' },
    setEarningWithHoldingConcept: () => {}, // No hace nada por defecto
    paidTaxes: [], // Inicialmente vacío
    updatePaidTax: () => {}, // No hace nada por defecto
    cleanAll: () => {}, // No hace nada por defecto
    updateTotalWithoutTaxes: () => {},
    totalTaxes:Big(0),
    vatNotTaxed:Big(0),
    exemptTax:Big(0),
    updateExemptTaxAmount: () => {},
    updateVatNotTaxedAmount: () => {},
    taxesList: [],
    setTaxesList: () => {},
    convertDuplicateTaxes: () => [],
    convertDuplicateItems: () => []
});

export default function TaxesPaidProvider(props: { children: React.ReactNode }) {
    const [totalWithoutTaxes, setTotalWithoutTaxes] = useState(Big(0));
    const [totalTaxes, setTotalTaxes] = useState(Big(0));
    const [totalToPay, setTotalToPay] = useState(Big(0));
    const [vatNotTaxed, setVatNotTaxed] = useState(Big(0));
    const [exemptTax, setExemptTax] = useState(Big(0));
    
    const [earningWithHoldingConcept, setEarningWithHoldingConcept] = useState<EarningWithholdingConceptDTO>({
        withholdingconcept: 0,
        description: '-',
    });

    const [paidTaxes, setPaidTaxes] = useState<PaidTax[]>([]);
    const [taxesList, setTaxesList] = useState<TaxType[]>([]); //Lista que se muestra en OrderOfProvisionTaxes

    const updatePaidTax = (taxtype: number, taxAccount:number, taxName:string, newAmount: Big, taxAccountName:string, plusTaxes:boolean) => {
        if (newAmount)
            setPaidTaxes((prev) => {
                const existingTaxIndex = prev.findIndex((tax) => tax.taxtype === taxtype);
                if (existingTaxIndex !== -1) {
                    const updatedTaxes = [...prev];
                    if (newAmount != Big(0) && plusTaxes)
                        newAmount = newAmount.plus(updatedTaxes[existingTaxIndex].amount)
                    updatedTaxes[existingTaxIndex] = { taxtype, amount: newAmount, taxAccount, taxName, taxAccountName};
                    return updatedTaxes;
                }
                return [...prev, { taxtype, amount: newAmount, taxAccount, taxName, taxAccountName}];
            });
    };

    const updateTotalWithoutTaxes = (newAmount: Big) => {
        setTotalWithoutTaxes((prev) => prev.plus(newAmount));
    };
    const updateExemptTaxAmount = (newAmount: Big) => {
        setExemptTax(newAmount);
    };
    const updateVatNotTaxedAmount = (newAmount: Big) => {
        setVatNotTaxed(newAmount ? newAmount : Big(0));
    };

    function convertDuplicateTaxes(taxes:PaidTax[] | undefined) {
        const _notDuplicates: PaidTax[] = []
        if (taxes) {
            const _taxes = taxes.map(tax => ({ ...tax }))
            _taxes.forEach((tax: PaidTax) => {
                const foundDuplicate = _notDuplicates.find((t) => t.taxAccount === tax.taxAccount)
                if (!foundDuplicate) {
                    _notDuplicates.push(tax)
                } else {
                    const newValue = foundDuplicate?.amount.plus(tax?.amount ? tax.amount : 0)
                    foundDuplicate.amount = newValue
                }
            })
        }
        return _notDuplicates
    }

    function convertDuplicateItems(items: ItemVaucher[] | undefined) {
        const _notDuplicates: ItemVaucher[] = []
        if (items) {
            const _items = items.map((item: ItemVaucher) => ({ ...item }))
            _items.forEach((item: ItemVaucher) => {
                const foundDuplicate = _notDuplicates.find((i) => i.account?.accountingaccount == item.account?.accountingaccount)
                if (!foundDuplicate) {
                    _notDuplicates.push(item)
                } else {
                    const newValue = foundDuplicate.amount.plus(item?.amount ? item.amount : 0)
                    foundDuplicate.amount = newValue
                }
            })
        }
        return _notDuplicates
    }

    useEffect(() => {
        setTotalTaxes(() => {
            return paidTaxes.reduce((sum, tax) => {
                const taxFromList = taxesList.find(t => t.taxType === tax.taxtype);
        
                if (taxFromList && !(taxFromList?.exemptVatTax || taxFromList?.vatNotTaxed)) {
                    return sum.plus(tax.amount);
                }
                return sum;
            }, new Big(0));
        });
    }, [paidTaxes]);

    useEffect(() => {
        setTotalToPay(totalTaxes.plus(totalWithoutTaxes).plus(vatNotTaxed ? vatNotTaxed : 0).plus(exemptTax));
    }, [totalWithoutTaxes, totalTaxes, exemptTax, vatNotTaxed]);

    const cleanAll = () => {
        setTotalWithoutTaxes(Big(0));
        setTotalToPay(Big(0));
        setExemptTax(Big(0))
        setVatNotTaxed(Big(0))
        setTotalTaxes(Big(0))
        setPaidTaxes([]);
    };

    return (
        <TaxesPaidContext.Provider
            value={{
                totalWithoutTaxes,
                totalToPay,
                setTotalToPay,
                earningWithHoldingConcept,
                setEarningWithHoldingConcept,
                paidTaxes,
                updatePaidTax,
                cleanAll,
                updateTotalWithoutTaxes,
                totalTaxes,
                vatNotTaxed,
                exemptTax,
                updateExemptTaxAmount,
                updateVatNotTaxedAmount,
                taxesList,
                setTaxesList,
                convertDuplicateTaxes,
                convertDuplicateItems
            }}
        >
            {props.children}
        </TaxesPaidContext.Provider>
    );
}

export const useTaxesPaid = () => useContext(TaxesPaidContext)